import React, { useState, useMemo, useEffect } from 'react';

import './alarmModal.module.css';
import { AlarmsHistoryFilter } from './AlarmsHistoryFilter';
import { getAlarmsColumns, AlarmModalContent } from './shared';
import styles from './styles.module.css';
import BellIcon from '@/assets/icons/bell.svg?react';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import { ALARM_TYPES, determineAlarmType } from '@/constants/turbines';
import useModal from '@/context/ModalContext/useModal';
import { AlarmData } from '@/slices/types';
import { pulseColors } from '@/solaris-lens-common-lib/src/styles/colors';
import { formatToDateTimeLocal } from '@/solaris-lens-common-lib/src/utils/dates';

interface HistoryLogContentProps {
  historyData?: AlarmData[] | any;
  isLoading?: boolean;
  error?: Error | null;
  onFilterChange?: (startDate: string, endDate: string) => void;
}

export default function HistoryLogContent({
  historyData,
  isLoading = false,
  error = null,
  onFilterChange
}: HistoryLogContentProps) {
  const { updateModalContent, closeModal } = useModal();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const defaultEndDate = useMemo(() => {
    const date = new Date();
    return formatToDateTimeLocal(date.toISOString());
  }, []);

  const defaultStartDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return formatToDateTimeLocal(date.toISOString());
  }, []);

  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);

  const historyLogs = useMemo(() => {
    if (!historyData) {
      return [];
    }

    let dataArray: any[] = [];

    if (Array.isArray(historyData)) {
      dataArray = historyData;
    } else if (historyData && typeof historyData === 'object') {
      if (
        'alarms' in historyData &&
        Array.isArray((historyData as any).alarms)
      ) {
        dataArray = (historyData as any).alarms;
      } else if ('data' in historyData) {
        const dataObj = historyData as { data?: any };
        if (Array.isArray(dataObj.data)) {
          dataArray = dataObj.data;
        } else if (
          dataObj.data &&
          typeof dataObj.data === 'object' &&
          'alarms' in dataObj.data &&
          Array.isArray((dataObj.data as any).alarms)
        ) {
          dataArray = (dataObj.data as any).alarms;
        }
      }
    }

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return [];
    }

    return dataArray.map((alarm: any, index: number) => {
      const alarmType = determineAlarmType(alarm);

      const dateValue =
        alarm.startDate || alarm.date || alarm.createdAt || alarm.timestamp;

      let durationValue = alarm.duration || 0;
      if (typeof durationValue === 'string') {
        durationValue = 0;
      }

      const isAcknowledged = Boolean(
        alarm.ackBy || alarm.ackAt || alarm.acknowledged || alarm.acknowledgedAt
      );

      return {
        id: alarm.id || `history-${index}`,
        name:
          alarm.label ||
          alarm.name ||
          alarm.type ||
          alarm.tag ||
          alarm.description ||
          '',
        description: alarm.description || '',
        date: dateValue,
        startDate: dateValue,
        acknowledged: isAcknowledged,
        duration: durationValue,
        type: alarmType,
        tag: alarm.tag || alarm.type || '',
        ackBy: alarm.ackBy || null,
        ackAt: alarm.ackAt || alarm.acknowledgedAt || null,
        label: alarm.label || alarm.name || alarm.type || alarm.tag || ''
      } as AlarmData;
    });
  }, [historyData]);

  const handleDetailsClick = (alarm: AlarmData) => {
    const modalContent = (
      <AlarmModalContent alarm={alarm} onClose={closeModal} />
    );

    updateModalContent(modalContent, {
      modalTitle: 'Alarm Description',
      modalIcon: BellIcon,
      className: 'alarmsModal'
    });
  };

  const columns = useMemo(
    () => getAlarmsColumns(handleDetailsClick),
    [handleDetailsClick]
  );

  useEffect(() => {
    setIsLocalLoading(isLoading);
  }, [isLoading]);

  const handleLoad = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onFilterChange && startDate && endDate) {
      const startISO = new Date(startDate).toISOString();
      const endISO = new Date(endDate).toISOString();
      setIsLocalLoading(true);
      onFilterChange(startISO, endISO);
      setTimeout(() => setIsLocalLoading(false), 100);
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
  };

  return (
    <div className={`${styles.alarmsContainer} ${styles.historyLogContainer}`}>
      <AlarmsHistoryFilter
        startDate={startDate}
        endDate={endDate}
        defaultEndDate={defaultEndDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onLoad={handleLoad}
        isLoading={false}
      />

      {isLocalLoading || isLoading ? (
        <div className={styles.loaderContainer}>
          <ContainerLoader />
        </div>
      ) : error ||
        (!isLoading && !isLocalLoading && historyLogs.length === 0) ? (
        <EmptyState
          title="No data available"
          subtitle="No data available for this tab."
        />
      ) : (
        <DataTable
          rows={historyLogs}
          columns={columns}
          className={`${styles.alarmsTable} ${styles.historyLogTable}`}
          initialState={{
            sorting: {
              field: 'date',
              sort: 'desc'
            }
          }}
          getRowStyle={(row: AlarmData) => {
            const style: React.CSSProperties = {};
            const statusBarColors = {
              [ALARM_TYPES.ALERT]: pulseColors.red2,
              [ALARM_TYPES.WARNING]: pulseColors.warning,
              AlertAndAcknowledged: pulseColors.lightRed3,
              WarningAndAcknowledged: pulseColors.lightWarning4
            };
            const getStatusBarColor = () => {
              if (row.type === ALARM_TYPES.ALERT) {
                if (row.acknowledged) {
                  return statusBarColors.AlertAndAcknowledged;
                }
                return statusBarColors[ALARM_TYPES.ALERT];
              }
              if (row.type === ALARM_TYPES.WARNING) {
                if (row.acknowledged) {
                  return statusBarColors.WarningAndAcknowledged;
                }
                return statusBarColors[ALARM_TYPES.WARNING];
              }
              return '';
            };
            const statusBarColor = getStatusBarColor();
            if (statusBarColor) {
              style.borderLeft = `4px solid ${statusBarColor}`;
              style.paddingLeft = '8px';
            }

            if (row.acknowledged) {
              style.backgroundColor = pulseColors.gray6 || pulseColors.gray9;
            } else if (row.type === ALARM_TYPES.ALERT) {
              style.backgroundColor = pulseColors.lightRed2;
            } else if (row.type === ALARM_TYPES.WARNING) {
              style.backgroundColor = pulseColors.lightYellow;
            }
            return style;
          }}
        />
      )}
    </div>
  );
}
